import React, {
  forwardRef,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { MuiThemeProvider, TablePagination } from '@material-ui/core'

import Table from './Table'
import objectPath from 'object-path'
import Title from './render/Title'
import Designer from 'designer/external/Designer'
import { DesignContext } from 'contexts/DesignContext'
import { checkTableCondition } from './Render'
import TableOverrideStyle from './TableOverrideStyle'

const PanelTable = ({
  title,
  editable,
  tableAction,
  tableColumn,
  dataTable,
  tableRef,
  uischema,
  renderTableData,
  tableColumnList,
  loading,
  rowsPerPageOptions,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  totalCount,
  addActionRef,
  dataRowClicked,
  isPagination,
  allParams,
  rowPanelAdd,
  setRowPanelAdd,
  fieldKey,
  fieldName,
  renderSummaryRow,
  tableTheme,
  localization,
}) => {
  const designContext = useContext(DesignContext)
  const isRenderComponent = objectPath.get(uischema, 'action.renderComponent')
  const refColumnId = objectPath.get(uischema, 'action.refColumnId')
  const rowSelectId = isRenderComponent
    ? objectPath.get(allParams, refColumnId)
    : undefined
  const conditionFormData = designContext?.jsonForm?.formData
  const conditionDatasourceValue = objectPath.get(designContext, '@datasource')
  const conditionContext = useMemo(
    () => ({
      jsonForm: {
        formData: conditionFormData,
      },
      '@datasource': conditionDatasourceValue,
    }),
    [conditionFormData, conditionDatasourceValue],
  )
  const formPanel = objectPath.get(uischema, 'tableAction.formPanel') || {}
  const newCount = rowPanelAdd.length
  const allAdd = totalCount + newCount
  const panelData = useMemo(
    () => dataTable.concat(rowPanelAdd),
    [dataTable, rowPanelAdd],
  )

  const rowStyle = useCallback((rowData) => {
    let found = null
    tableColumnList.forEach((column) => {
      if (column.conditions) {
        let rowValue = objectPath.get(rowData, column.field)
        found = checkTableCondition(conditionContext, column, rowValue)
      }
    })
    if (rowData?.tableData?.id === dataRowClicked?.tableData?.id) {
      return {
        backgroundColor: uischema.action?.selectColor || '#dddddd',
        color: uischema.action?.selectTextColor || '#fff',
      }
    }

    if (isRenderComponent) {
      let rowId = objectPath.get(rowData, refColumnId)
      if (rowSelectId === rowId) {
        return {
          backgroundColor: uischema.action?.selectColor || '#dddddd',
          color: uischema.action?.selectTextColor || '#fff',
        }
      }
    }

    if (found) {
      return {
        backgroundColor: found.rowColor,
        color: found.rowTextColor,
      }
    }
    return {}
  }, [
    conditionContext,
    dataRowClicked?.tableData?.id,
    isRenderComponent,
    refColumnId,
    rowSelectId,
    tableColumnList,
    uischema.action?.selectColor,
    uischema.action?.selectTextColor,
  ])

  const tableOptions = useMemo(
    () => ({
      paging: uischema.isPaging === undefined ? true : uischema.isPaging,
      pageSizeOptions: rowsPerPageOptions,
      addRowPosition: 'first',
      search: uischema?.tableAction?.searchable,
      pageSize: rowsPerPage || uischema?.rowsPerPage || 5,
      sorting: true,
      rowStyle,
    }),
    [
      rowStyle,
      rowsPerPage,
      rowsPerPageOptions,
      uischema.isPaging,
      uischema?.rowsPerPage,
      uischema?.tableAction?.searchable,
    ],
  )

  return (
    <MuiThemeProvider theme={tableTheme}>
      <TableOverrideStyle
        title={title}
        fieldKey={fieldKey}
        uischema={uischema}
      />
      <div
        id={fieldKey}
        style={
          uischema.isPaging ? {} : { display: 'inline-grid', width: '100%' }
        }
      >
        <Table
          id={fieldKey}
          name={fieldName}
          tableIcons={{
            Add: forwardRef((props, ref) => <div ref={addActionRef}></div>),
          }}
          isLoading={loading}
          title={<Title title={title} />}
          editable={editable}
          actions={tableAction}
          tableRef={tableRef}
          columns={tableColumn}
          data={panelData}
          localization={localization}
          detailPanel={({ rowData }) => {
            return (
              <div style={{ width: 'fit-content' }}>
                <Designer
                  code={{ form: formPanel.formCode }}
                  component={{
                    data: rowData,
                  }}
                  tableProps={{
                    isDetailPanel: true,
                    renderTableData: () => {
                      if (rowPanelAdd.length > 0) {
                        setRowPanelAdd([])
                      }
                      renderTableData()
                    },
                  }}
                />
              </div>
            )
          }}
          options={tableOptions}
          components={
            isPagination && {
              Pagination: (props) => (
                <TablePagination
                  {...props}
                  rowsPerPageOptions={rowsPerPageOptions}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={(event, newPage) =>
                    handleChangePage(newPage, rowsPerPage)
                  }
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  count={allAdd}
                />
              ),
            }
          }
          renderSummaryRow={renderSummaryRow}
        />
      </div>
    </MuiThemeProvider>
  )
}

export default PanelTable
